import { useEffect, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { exportApi } from '../../api/services';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

// Handover Mode (Phase 3 task 3.5 second slice). Collects the optional cover
// EA name + free-text notes, then downloads the consolidated handover pack
// PDF. Mirrors the WeeklyExportDialog pattern: blob response, filename from
// content-disposition with rfc5987 fallback, errors surfaced inline so the
// EA's notes aren't lost on transport failure.

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
}

function filenameFromContentDisposition(headerValue: string | undefined, fallback: string): string {
  if (!headerValue) return fallback;
  const rfc5987 = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
  if (rfc5987?.[1]) return decodeURIComponent(rfc5987[1].trim().replace(/"/g, ''));
  const legacy = headerValue.match(/filename="?([^"]+)"?/i);
  return legacy?.[1] ?? fallback;
}

interface HandoverPackDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function HandoverPackDialog({ open, onClose }: HandoverPackDialogProps) {
  const [coverEaName, setCoverEaName] = useState('');
  const [notesForCoverEa, setNotesForCoverEa] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
  }, [open]);

  const handleExport = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        coverEaName: coverEaName.trim() || undefined,
        notesForCoverEa: notesForCoverEa.trim() || undefined,
      };
      const response = await exportApi.downloadHandoverPackPdf(payload);
      const blob = response.data as Blob;
      const today = new Date().toISOString().slice(0, 10);
      const filename = filenameFromContentDisposition(
        response.headers?.['content-disposition'] as string | undefined,
        `Cadence-Handover-${today}.pdf`,
      );
      triggerBrowserDownload(blob, filename);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not generate the handover pack. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate handover pack</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          One consolidated PDF you can forward to your cover EA — every principal you cover,
          their next seven days of plans, upcoming OOO, and open delegations.
        </p>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cover-ea-name">Cover EA name (optional)</Label>
            <Input
              id="cover-ea-name"
              value={coverEaName}
              onChange={(e) => setCoverEaName(e.target.value)}
              disabled={busy}
              placeholder="e.g. Sam Patel"
              maxLength={200}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cover-ea-notes">Notes for cover EA (optional)</Label>
            <Textarea
              id="cover-ea-notes"
              rows={5}
              value={notesForCoverEa}
              onChange={(e) => setNotesForCoverEa(e.target.value)}
              disabled={busy}
              placeholder="Anything that isn't in the data — names to watch, recurring escalations, principal preferences for the week."
              maxLength={4000}
            />
            <p className="text-xs text-muted-foreground">
              Up to 4,000 characters. Rendered on the cover sheet.
            </p>
          </div>
        </div>
        {error && (
          <Alert variant="error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            {busy ? 'Generating…' : 'Download PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
